import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  BuildingLibraryIcon,
  CalendarDaysIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const History = () => {
  return (
    <main id="main-content" className="bg-white">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Our School
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                History of <span className="font-bold">P.S. 11X</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                A historic landmark school with over 130 years of educational excellence in the Bronx.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Historic <span className="font-bold text-[#630503]">School Building</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
          </div>

          <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Located on Ogden Avenue between Merriam Avenue and West 169th Street in the Bronx, Public School 11 (until 1898 Public School 91), is an exceptional Romanesque Revival academic building and one of the finest examples of the style in The Bronx. Built in 1889 of brick and Harlem River stone, on the site of an 1852 school, P.S. 11 was designed by George W. Debevoise, the Superintendent of Public School Buildings for the New York City Board of Education between 1884 and 1891.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Building Sections */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Building <span className="font-bold text-[#630503]">Architecture</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 1889 Section */}
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] to-[#8B4513]"></div>
              <div className="mb-6">
                <div className="w-14 h-14 bg-[#630503]/10 rounded-xl flex items-center justify-center mb-4">
                  <BuildingLibraryIcon className="h-8 w-8 text-[#630503]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">1889 Original Building</h3>
                <div className="w-16 h-px bg-[#630503]/40 mb-6"></div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                A three-story northern section with tower and rear extension built in 1889, forming the foundation of our historic campus.
              </p>
            </div>

            {/* 1905 Wing */}
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B4513] to-[#A0522D]"></div>
              <div className="mb-6">
                <div className="w-14 h-14 bg-[#8B4513]/10 rounded-xl flex items-center justify-center mb-4">
                  <CalendarDaysIcon className="h-8 w-8 text-[#8B4513]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">1905 Expansion</h3>
                <div className="w-16 h-px bg-[#8B4513]/40 mb-6"></div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                A six-bay, three-story wing built in 1905 to accommodate the growing student population and educational needs.
              </p>
            </div>

            {/* 1930 Gymnasium */}
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#A0522D] to-[#630503]"></div>
              <div className="mb-6">
                <div className="w-14 h-14 bg-[#A0522D]/10 rounded-xl flex items-center justify-center mb-4">
                  <MapPinIcon className="h-8 w-8 text-[#A0522D]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">1930 Gymnasium</h3>
                <div className="w-16 h-px bg-[#A0522D]/40 mb-6"></div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                A gymnasium/auditorium built in 1930, featuring a mural added in 1937 as part of the Works Progress Administration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Historic Recognition Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Historic <span className="font-bold text-[#630503]">Recognition</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
          </div>

          <div className="bg-[#630503]/5 rounded-xl p-8 lg:p-12 shadow-xl border border-[#630503]/20">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The interior of the auditorium has a mural added in 1937 as part of a national art project, known as the Works Progress Administration. Installations of this art project can be found all throughout New York City. P.S. 11x was listed on the National Register of Historic Places in 1983. It was designated a New York City Landmark in 1981.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Public School 11 is one of the few surviving 19th-century school buildings in the Bronx, a major work by George W. Debevoise, and it is one of the borough's notable examples of the Romanesque Revival. It continues to add distinction and interest to this section of the city as it has since its completion nearly a century ago.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Campus Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Modern <span className="font-bold text-[#630503]">Campus</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
          </div>

          <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed">
                Through the years, P.S. 11x has become 2 buildings that stand proudly together, side by side a quarter mile apart on Ogden Ave. The historic main building houses grades 2-5 and the early childhood satellite building, known as the "annex" is 3K-1st Grade. Through public advocacy and community leadership, the early childhood building was built to support the needs of early childhood!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* High Bridge History Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              History of the <span className="font-bold text-[#630503]">High Bridge</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              The historic bridge that gives our school and community its name
            </p>
          </div>

          <div className="space-y-8">
            {/* Early Construction */}
            <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] to-[#8B4513]"></div>
              
              <div className="prose prose-lg max-w-none">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">The Croton Aqueduct System</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The High Bridge was built in the mid-19th century as part of the Croton Aqueduct system, which carried water from the Croton River in Westchester down to Manhattan. When you cross the bridge, you will be walking above the aqueduct's original pipe, which still lies beneath the walkway of the bridge.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The bridge opened to carry the aqueduct across the Harlem River in 1848, and its walkway was completed in 1864, making it a popular spot to promenade on a nice day.
                </p>
              </div>
            </div>

            {/* Golden Age */}
            <div className="bg-[#630503]/5 rounded-xl p-8 lg:p-12 shadow-xl border border-[#630503]/20">
              <div className="prose prose-lg max-w-none">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">A 19th Century Attraction</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The bridge achieved fame as an attraction for New Yorkers and tourists and a favorite subject for artists and photographers, a sort of 19th century High Line. The walkway's popularity led to the building of hotels, restaurants and amusement parks in the vicinity.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Equally popular were boat cruises up and down the river, and racing competitions for crew boats. Later, once the Harlem River Speedway was opened in 1898, sightseers strolled along the new waterfront esplanade in the cool breezes and watched horses and buggies fly by.
                </p>
              </div>
            </div>

            {/* Decline and Revival */}
            <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B4513] to-[#A0522D]"></div>
              
              <div className="prose prose-lg max-w-none">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Decline and Revival</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  After construction of the Major Deegan Expressway in 1956 and the Harlem River Drive in 1964, public use of the waterfront faded. The river became polluted, paths were blocked, and the pull of the parks on the water's edge vanished. In the 1970s, public access to the bridge was discontinued.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Local pressure to reopen the bridge began soon after, and eventually, groups such as The High Bridge Coalition were able to coalesce that support into a citizen-led campaign to restore the High Bridge and its neighboring parks. In 2012, we began rehabilitating the bridge, and it was reopened in June 2015.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Complete Timeline Section */}
      <section id="timeline" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-20">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Complete Timeline
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Our Journey Through Time
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              A detailed timeline of key milestones in the history of P.S. 11X and the Highbridge community.
            </p>
          </div>
          
          {/* Timeline */}
          <div className="relative">
            {/* Central Timeline Line - Desktop */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#630503] via-[#8B4513] to-[#A0522D] transform -translate-x-1/2"></div>
            
            {/* Timeline Items */}
            <div className="space-y-12 lg:space-y-24">
              {[
                { year: "1864", title: "High Bridge Built", description: "High Bridge built in New York City by Irish Immigrants in 1864." },
                { year: "1889", title: "School Founding", description: "A three-story northern section with tower and rear extension built in 1889; historic bell hung in front of the building." },
                { year: "1905", title: "Building Expansion", description: "A six-bay, three-story wing built in 1905." },
                { year: "1930", title: "Gymnasium Added", description: "Gymnasium/auditorium built to enhance student activities and community events." },
                { year: "1937", title: "WPA Mural", description: "The interior of the auditorium has a mural added in 1937 as part of a national art project, known as the Works Progress Administration. Installations of this art project can be found all throughout New York City." },
                { year: "1970", title: "Community Advocacy", description: "Parents protested the overcrowding of schools and demand a resolution. Led by Highbridge Heroes Joanne Smitherman, Marjorie Dunbar, and community activists this protest led to the opening of a new school, P.S. 126!" },
                { year: "1973", title: "Birth of Hip Hop", description: "On August 11, 1973 at 1520 Sedgwick Ave, Hip Hop was born. DJ Kool Herc, a Jamaican immigrant, used two turntables to extend breakbeats—planting the seeds of a new sound. Hip-hop emerged as a cultural force from communities facing poverty, neglect, and systemic oppression, becoming a voice for the unheard. Highbridge is known as the birthplace of Hip Hop—yet another example of how the Bronx has always been brilliant and will always be brilliant." },
                { year: "1981", title: "NYC Landmark", description: "It was designated a New York City Landmark in 1981." },
                { year: "1983", title: "National Register", description: "P.S. 11x was listed on the National Register of Historic Places in 1983." },
              { year: "1996", title: "Early Childhood Building (09X172)", description: "The Early Childhood Building becomes home to our 3K–1st Graders of P.S. 11X." },
              { year: "2013", title: "Highbridge Green", description: "Community Advocacy: Highbridge Green Middle School is a result of a sustained effort by community organizations, churches, and residents who sought a middle school for their children. Many P.S. 11x scholars attend Highbridge Green after elementary school." }
              ].map((item, index) => (
                <div key={index} className="relative">
                  {/* Mobile/Tablet Layout */}
                  <div className="lg:hidden">
                    <div className="flex items-start">
                      {/* Timeline Dot */}
                      <div className="flex-shrink-0 w-12 h-12 bg-[#630503] rounded-full flex items-center justify-center mr-6 mt-2">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                      {/* Content */}
                      <div className="flex-1 bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="inline-block bg-[#630503] text-white px-4 py-2 text-sm font-bold mb-6 rounded-lg">
                          {item.year}
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    {/* Connecting Line */}
                    {index < 9 && (
                      <div className="w-1 h-16 bg-[#630503]/30 ml-6 mt-4"></div>
                    )}
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden lg:block">
                    {(() => {
                      const imageSrc = {
                        '1864': '/assets/images/history/highbridge1864.jpg',
                        '1889': '/assets/images/history/school1889.png',
                        '1905': '/assets/images/history/school1905.jpg',
                        '1973': '/assets/images/history/hiphop1973.jpg',
                      }[item.year];

                      const contentCard = (
                        <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 relative">
                          {/* Timeline Connector to Center Line */}
                          <div className={`absolute top-1/2 ${index % 2 === 0 ? '-right-14' : '-left-14'} w-12 h-0.5 bg-[#630503] transform -translate-y-1/2`}></div>

                          <div className="inline-block bg-[#630503] text-white px-5 py-3 text-sm font-bold mb-6 rounded-lg shadow-md">
                            {item.year}
                          </div>
                          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5">{item.title}</h3>
                          <p className="text-gray-600 leading-relaxed text-sm sm:text-base lg:text-lg break-words">{item.description}</p>

                          {/* Visual accent */}
                          <div className="mt-6 w-16 h-1 bg-gradient-to-r from-[#630503] to-gray-200 rounded-full"></div>
                        </div>
                      );

                      const imageCard = imageSrc ? (
                        <div className="overflow-hidden rounded-xl shadow-lg border border-gray-100 bg-white hover:shadow-xl transition-all duration-300">
                          <img src={imageSrc} alt={`${item.year} related`} className="w-full h-64 object-cover" />
                        </div>
                      ) : (
                        <div className="w-full h-0" />
                      );

                      return (
                        <div className="flex items-center justify-between">
                          <div className={`w-5/12 pr-12`}>
                            {index % 2 === 0 ? contentCard : imageCard}
                          </div>
                          <div className={`w-5/12 pl-12`}>
                            {index % 2 !== 0 ? contentCard : imageCard}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Timeline Dot on Center Line */}
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-white border-4 border-[#630503] rounded-full shadow-lg"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default History; 